import 'simplebar-react/dist/simplebar.min.css'

import styled from '@emotion/styled'
import { ANSWER_TYPE } from '@mathflat/domain/@entities/Problem/constants'
import { s3URL } from '@mathflat/shared/@common/utils/s3'
import { debounce } from 'lodash-es'
import { observer } from 'mobx-react'
import { useCallback, useMemo, useState } from 'react'
import { useLocation } from 'react-router'

import { RequestScoring } from '~/@common/api'
import { useStudentAppMediaQuery } from '~/@common/hooks/useMediaQuery'
import { CustomEventService } from '~/@common/services/event.service'
import modalService from '~/@common/services/modal.service'
import { colors, typo } from '~/@common/styles'
import { Icon } from '~/@common/ui/Icon/Icon'
import type { StudentWorkbookDetailService } from '~/@pages/student-workbook/detail/@service/StudentWorkbookDetail.service'

import { 일반채점입력 } from '../../(Scoring)/(일반채점)/일반채점입력'
import 자동채점입력 from '../../(Scoring)/(자동채점)/자동채점입력'
import type { 자동채점입력_주관식Props } from '../../(Scoring)/(자동채점)/자동채점입력/자동채점입력_주관식'
import { SubmittedAnswer, 채점결과_말풍선 } from '../../(Scoring)/(채점결과)/채점결과'
import type { ProblemScoring } from '../@trait/ProblemScoring.trait'
import type { ProblemScoringViewOption } from '../@trait/ProblemScoringViewOption.trait'
import ProblemScoringStyle from '../ProblemScoringCard/ProblemScoringCard.style'
import { AnswerVideoDrawer } from './_component/AnswerVideoDrawer/AnswerVideoDrawer'
import {
  getDrawerLabelVisibility,
  shouldShowAnswerInput,
  shouldShowDrawerButton,
} from './ProblemScoringFooter.utils'

export type ProblemScoringFooterProps = {
  contentId?: string
  problemScoring: ProblemScoring<'WORKSHEET' | 'WORKBOOK'>
  viewOption?: ProblemScoringViewOption<'학습모듈' | 'NOT_학습모듈'>
  isSubmittedAnswerAvailable?: boolean
  onSubmitVideoAssist?: StudentWorkbookDetailService['onSubmitVideoAssist']
  onSubmitAnswerImmediately?: (problemScoring: ProblemScoring<'WORKSHEET' | 'WORKBOOK'>) => void
  onLocalUserInputChange?: (
    localUserInput: RequestScoring.자동채점 | RequestScoring.일반채점,
  ) => void
} & Pick<자동채점입력_주관식Props, 'virtualKeybaordShowType'>

const ProblemScoringFooter = observer(
  ({
    contentId,
    problemScoring,
    viewOption,
    virtualKeybaordShowType,
    isSubmittedAnswerAvailable = true,
    onSubmitVideoAssist,
    onSubmitAnswerImmediately,
    onLocalUserInputChange,
  }: ProblemScoringFooterProps) => {
    const [openDrawer, setOpenDrawer] = useState(false)
    const { isMobile } = useStudentAppMediaQuery()
    const { pathname } = useLocation()

    const handleSubmitAnswerImmediately = useCallback(
      (problemScoring: ProblemScoring<'WORKSHEET' | 'WORKBOOK'>) => {
        if (!onSubmitAnswerImmediately) return
        onSubmitAnswerImmediately(problemScoring)
      },
      [onSubmitAnswerImmediately],
    )
    const handleDebouncedSubmitAnswerImmediately = useMemo(() => {
      return debounce(handleSubmitAnswerImmediately, 1000)
    }, [handleSubmitAnswerImmediately])

    const handleLocalUserInputChange = useCallback(
      (localUserInput: RequestScoring.자동채점 | RequestScoring.일반채점) => {
        if (!onLocalUserInputChange) return
        onLocalUserInputChange(localUserInput)
      },
      [onLocalUserInputChange],
    )

    const handleDebouncedLocalUserInputChange = useMemo(() => {
      return debounce(handleLocalUserInputChange, 1000)
    }, [handleLocalUserInputChange])

    if (!viewOption) return <></>
    if (viewOption.isExamInProgress && problemScoring.teacherScoringFlag) {
      return <></>
    }
    const isAnswerInputView = shouldShowAnswerInput(viewOption, problemScoring)
    const isDrawerButtonView = shouldShowDrawerButton({
      viewOption,
      problemScoring,
      isSubmittedAnswerAvailable: !!isSubmittedAnswerAvailable,
    })
    if (!isAnswerInputView && !isDrawerButtonView) return <></>

    if (isDrawerButtonView) {
      // isDrawerButtonView 는 푸터 렌더 자체의 책임 (예: 자동채점 제출 후 "내가 쓴 답" 표시).
      // drawer trigger(">") 노출은 utils 의 getDrawerLabelVisibility 가 결정 — 라벨 0 이면 trigger 도 미노출.
      const { showVideoLabel, showHandwritingLabel, showSolutionLabel } = getDrawerLabelVisibility({
        viewOption,
        problemScoring,
        isSubmittedAnswerAvailable: !!isSubmittedAnswerAvailable,
      })
      const showDrawerTrigger = showVideoLabel || showHandwritingLabel || showSolutionLabel
      const drawerText = (
        <>
          {showVideoLabel && <span className="explation-submitted-answer-text">풀이동영상</span>}
          {showHandwritingLabel && <span className="explation-submitted-answer-text">내풀이</span>}
          {showSolutionLabel && <span className="explation-submitted-answer-text">해설</span>}
        </>
      )

      // 자기주도(챌린지, 오답심화)가 아닐 때 + 학부모 뷰 아닐 때만 문제해설이미지 가드 적용.
      // 학부모는 미풀이 시에도 "내가 쓴 답 : -" 표기를 위해 푸터를 항상 렌더해야 함.
      if (!viewOption.isSelfLearning && !viewOption.isParentView && !problemScoring.문제해설이미지)
        return <></>
      return (
        <S.ProblemScoringFooter
          data-tooltip-id={`submitted-answer-${problemScoring.id}`}
          onClick={() => {
            showDrawerTrigger && setOpenDrawer((prev) => !prev)
          }}
          className="has-padding"
        >
          {!!problemScoring.제출한답 && (
            <span
              className="left"
              onClick={(e) => {
                e.stopPropagation()
                if (isMobile) {
                  modalService.openModal(
                    <ProblemScoringStyle.MobileModalContainer>
                      <SubmittedAnswer
                        채점결과={problemScoring.채점결과}
                        제출한답={problemScoring.제출한답}
                        문제정답타입={problemScoring.문제정답타입}
                        ellipse={false}
                      />
                    </ProblemScoringStyle.MobileModalContainer>,
                    { modalName: '제출한 답 모달', hasCloseButton: true },
                  )
                } else {
                  CustomEventService.tooltipOn.dispatch(`submitted-answer-${problemScoring.id}`, e)
                }
              }}
            >
              <span className="answer-text">내가 쓴 답 :&nbsp;</span>

              <채점결과_말풍선
                id={`submitted-answer-${problemScoring.id}`}
                value={problemScoring.채점결과}
                문제정답타입={problemScoring.문제정답타입}
                제출한답={problemScoring.제출한답}
              />
              <SubmittedAnswer
                채점결과={problemScoring.채점결과}
                제출한답={problemScoring.제출한답}
                문제정답타입={problemScoring.문제정답타입}
              />
            </span>
          )}

          {/* 학부모 뷰 — EXAM/WORKSHEET 무관 답 null → "정답 미입력" (자기주도학습 제외) */}
          {viewOption.isParentView &&
            !viewOption.isSelfLearning &&
            problemScoring.제출한답 === null && (
              <div className="left">
                <span className="answer-guide">정답 미입력</span>
              </div>
            )}
          {/* 학생 뷰 EXAM — 답 null → "정답 미입력", 답 있고 teacherScoringFlag → "선생님이 채점함" */}
          {!viewOption.isParentView && viewOption.content.kind === 'EXAM' && (
            <>
              {problemScoring.제출한답 === null ? (
                <div className="left">
                  <span className="answer-guide">정답 미입력</span>
                </div>
              ) : problemScoring.teacherScoringFlag ? (
                <div className="left">
                  <span className="answer-guide">선생님이 채점함</span>
                </div>
              ) : null}
            </>
          )}
          {/* 학생 뷰 WORKSHEET — 자동채점 + 답 미입력 시 "선생님이 채점함" (자기주도학습 제외) */}
          {!viewOption.isParentView &&
            viewOption.content.type !== 'EXAM' &&
            problemScoring.isAutoScoring &&
            problemScoring.제출한답 === null &&
            !viewOption.isSelfLearning && (
              <div className="left">
                <span className="answer-guide">선생님이 채점함</span>
              </div>
            )}

          {showDrawerTrigger && (
            <>
              <span className="right">
                {drawerText}
                <Icon name="icon_chevron_right" color={colors.gray.$500} size={20} />
              </span>
              <AnswerVideoDrawer
                contentId={contentId}
                isSubmittedAnswerAvailable={isSubmittedAnswerAvailable}
                viewOption={viewOption}
                openDrawer={openDrawer}
                problemScoring={problemScoring}
                onSubmitVideoAssist={onSubmitVideoAssist}
              />
            </>
          )}
        </S.ProblemScoringFooter>
      )
    }
    // 문제 : 제출 전
    // 학습지 or 교재 : 채점 가능 상태
    if (isAnswerInputView) {
      const answerDataTracking = () => {
        const trackingInfo = (() => {
          if (viewOption.content.kind === 'WORKSHEET' && viewOption.content.type === 'CUSTOM') {
            const isScoringPage = pathname.includes('/scoring')
            return {
              trackKey: !isScoringPage
                ? '[내학습지] 한문제씩_정답입력'
                : '[내학습지] 빠른채점_정답입력',
              trackData: {
                '학제/학년': problemScoring.학제학년학기,
              },
            }
          }
          if (viewOption.content.kind === 'WORKBOOK') {
            if (viewOption.content.type === 'CUSTOM') {
              return {
                trackKey: '[내교재] 빠른채점_정답입력',
                trackData: {
                  '학년/학기': problemScoring.학제학년학기,
                },
              }
            }
            if (viewOption.content.type === 'CUSTOM_SIGNATURE') {
              return {
                trackKey: '[시그니처교재] 빠른채점_정답입력',
                trackData: {
                  '학년/학기': problemScoring.학제학년학기,
                },
              }
            }
            if (viewOption.content.type === 'ACADEMY_GROUP') {
              return {
                trackKey: '[학원그룹교재] 빠른채점_정답입력',
                trackData: {
                  '학년/학기': problemScoring.학제학년학기,
                },
              }
            }
          }
          return null
        })()

        if (trackingInfo) {
          const 문제타입 =
            problemScoring.문제정답타입 === 'SINGLE_CHOICE' ||
            problemScoring.문제정답타입 === 'MULTIPLE_CHOICE'
              ? '객관식'
              : '주관식'
          window.freshpaint?.track(trackingInfo.trackKey, {
            문제타입,
            ...trackingInfo.trackData,
          })
        }
      }

      // 1. 자동채점
      // 1-1. 주관식
      if (problemScoring.localUserInput instanceof RequestScoring.자동채점) {
        if (problemScoring.문제정답타입 === 'SHORT_ANSWER') {
          if (!problemScoring.keypadTypes) return null
          const virtualKeyboardMarginBottom =
            isMobile && viewOption.content.type === 'CONCEPTUAL' ? 10 : undefined

          return (
            <S.ProblemScoringFooter className="has-padding">
              <자동채점입력.주관식
                id={problemScoring.id}
                keypadTypes={problemScoring.keypadTypes}
                schoolType={problemScoring.contentSchoolType}
                answerUnits={problemScoring.answerUnits}
                value={problemScoring.localUserInput.localUserAnswer ?? ''}
                onChange={(v) => {
                  const currentAnswer = (problemScoring.localUserInput as RequestScoring.자동채점)
                    .localUserAnswer
                  problemScoring.setLocalUserInput(v)
                  const newLocalUserInput = problemScoring.localUserInput as RequestScoring.자동채점
                  const newAnswer = newLocalUserInput.localUserAnswer

                  if (currentAnswer === null && newAnswer === '') {
                    handleDebouncedSubmitAnswerImmediately(problemScoring)
                    // 답 입력하지 않았지만 onChange 실행되는 케이스는 무시
                    return
                  }

                  if (currentAnswer !== newAnswer) {
                    handleDebouncedLocalUserInputChange(newLocalUserInput)
                    handleDebouncedSubmitAnswerImmediately(problemScoring)
                  }
                }}
                onCloseCallback={answerDataTracking}
                virtualKeybaordShowType={virtualKeybaordShowType}
                virtualKeyboardMarginBottom={virtualKeyboardMarginBottom}
              />
            </S.ProblemScoringFooter>
          )
        }

        if (problemScoring.문제n지선다) {
          // 1-2. 객관식
          const data = Array.from({ length: problemScoring.문제n지선다 }).map((_, index) => ({
            label: index + 1,
            value: index + 1,
          }))

          return (
            <S.ProblemScoringFooter className="has-padding">
              <자동채점입력.객관식
                circleClassName={viewOption.content.type === 'CONCEPTUAL' ? 'circle-sm' : undefined}
                data={data}
                values={
                  problemScoring.localUserInput.localUserAnswer === ''
                    ? []
                    : problemScoring.localUserInput.localUserAnswer?.split(',') ?? []
                }
                max={problemScoring.문제정답길이}
                onChange={(v) => {
                  problemScoring.setLocalUserInput(v.join(','))
                  const newLocalUserInput = problemScoring.localUserInput as RequestScoring.자동채점
                  handleLocalUserInputChange(newLocalUserInput)
                  answerDataTracking()

                  onSubmitAnswerImmediately?.(problemScoring)
                }}
              />
            </S.ProblemScoringFooter>
          )
        } else {
          console.error('문제n지선다 값이 없습니다.')
          return null
        }
      } else {
        // 2. 일반 채점
        return (
          <S.ProblemScoringFooter>
            <일반채점입력
              value={problemScoring.localUserInput?.localResult ?? ANSWER_TYPE.NONE}
              onChange={(v) => {
                problemScoring.setLocalUserInput(v)
                const newLocalUserInput = problemScoring.localUserInput as RequestScoring.일반채점
                handleLocalUserInputChange(newLocalUserInput)
                answerDataTracking()
              }}
            />
          </S.ProblemScoringFooter>
        )
      }
    }
  },
)

const S = {
  ProblemScoringFooter: styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex: 0 0 70px;
    background-color: white;
    border-bottom-left-radius: var(--Radius-300);
    border-bottom-right-radius: var(--Radius-300);
    ${typo.body02};

    > * {
      cursor: pointer;
    }
    &.has-padding {
      padding: 0 20px;
    }

    > .left,
    > .center {
      display: flex;
      align-items: center;
      width: 100%;
      height: 36px;

      font-weight: bold;
      color: ${colors.gray.$700};
      .answer-text {
        flex-shrink: 0;
      }

      .answer-guide {
        color: ${colors.gray.$600};
        font-weight: 400;
        ${typo.body01}
      }
    }

    > .center {
      justify-content: center;
    }

    > .right {
      display: flex;
      align-items: center;
      flex-shrink: 0;
      height: 36px;
      margin: 0 auto;
      cursor: pointer;

      color: ${colors.gray.$600};
    }
    .explation-submitted-answer-text + .explation-submitted-answer-text {
      &::before {
        content: '・';
      }
    }
  `,
  Thumbnail: styled.div`
    width: 100%;
    height: 100%;
    background: url(${s3URL.common('images/video/problem-solving/background.png')}) #fff;

    > .img-bg {
      position: absolute;
      top: 7%;
      left: 30%;
      padding: 4% 4% 0px 4%;

      width: 64%;
      height: 93%;
      background: #fff;
      box-shadow: 0px 4px 30px 0px #0000001a;
    }

    > .gradation {
      position: absolute;
      top: 50%;

      width: 100%;
      height: 50%;
      background: linear-gradient(180deg, rgba(251, 252, 255, 0) 0%, #fbfcff 72.4%);

      > .title {
        position: absolute;
        top: 40%;
        left: 7%;
      }
      > .sub-title {
        position: absolute;
        top: 20%;
        left: 7%;
      }
    }
  `,
}

export default ProblemScoringFooter
